import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Search } from "lucide-react";
import { floatingSheet, theme } from "../theme";
import type { PaletteEntry } from "./types";

// ── Command palette ───────────────────────────────────────────────────────
// Ctrl/Cmd+K in every shell. Sources: every visible nav item (section label
// as hint) plus the shell's extras (clients). Substring match, arrow keys,
// Enter, Escape.

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  entries: PaletteEntry[];
  /** Loaded once each time the palette opens (e.g. the consultant's client list). */
  extras?: () => Promise<PaletteEntry[]>;
}

const MAX_RESULTS = 10;

function matches(entry: PaletteEntry, query: string): boolean {
  if (!query) return true;
  const hay = [entry.label, entry.hint ?? "", ...(entry.keywords ?? [])].join(" ").toLowerCase();
  return query.split(/\s+/).filter(Boolean).every((term) => hay.includes(term));
}

export function CommandPalette({ open, onClose, entries, extras }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [extra, setExtra] = useState<PaletteEntry[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Opening resets the query and focuses the input. The extras load
  // separately, so a client list refreshing while the palette is open (which
  // gives `extras` a new identity) leaves the typed query alone and keeps the
  // current rows until the new list arrives.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open || !extras) { setExtra([]); return; }
    let cancelled = false;
    extras().then((list) => { if (!cancelled) setExtra(list); }).catch(() => { if (!cancelled) setExtra([]); });
    return () => { cancelled = true; };
  }, [open, extras]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [...entries, ...extra];
    const seen = new Set<string>();
    const out: PaletteEntry[] = [];
    for (const entry of all) {
      if (seen.has(entry.id)) continue;
      if (!matches(entry, q)) continue;
      seen.add(entry.id);
      out.push(entry);
      if (out.length >= MAX_RESULTS) break;
    }
    return out;
  }, [entries, extra, query]);

  useEffect(() => { setActive(0); }, [query, results.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  if (!open) return null;

  function run(entry: PaletteEntry) {
    onClose();
    if (entry.onSelect) entry.onSelect();
    else if (entry.to) navigate(entry.to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (results.length === 0 ? 0 : (i + 1) % results.length)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      const entry = results[active];
      if (entry) run(entry);
    }
  }

  const listboxId = "nav-palette-results";

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "80px 16px 16px" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Jump to"
        onKeyDown={onKeyDown}
        style={{
          ...floatingSheet, width: "100%", maxWidth: 520, overflow: "hidden",
          fontFamily: theme.font,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--c-line)" }}>
          <Search size={15} style={{ color: theme.color.faint, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a page or client"
            aria-label="Jump to"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={results[active] ? `nav-palette-${results[active].id}` : undefined}
            autoComplete="off"
            spellCheck={false}
            style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 14, color: theme.color.text, fontFamily: theme.font }}
          />
          <kbd style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: `1px solid ${theme.color.border}`, color: theme.color.faint }}>esc</kbd>
        </div>

        <div ref={listRef} id={listboxId} role="listbox" aria-label="Results" style={{ maxHeight: 360, overflowY: "auto" }}>
          {results.length === 0 ? (
            <div style={{ padding: 20, fontSize: 13, color: theme.color.faint, textAlign: "center" }}>Nothing matches</div>
          ) : (
            results.map((entry, i) => {
              const selected = i === active;
              return (
                <button
                  key={entry.id}
                  id={`nav-palette-${entry.id}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-index={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(entry)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, width: "100%",
                    padding: "10px 16px", background: selected ? theme.color.accentBg : "none", border: "none",
                    borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer", textAlign: "left",
                    fontFamily: theme.font,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: selected ? theme.color.accentText : theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {entry.label}
                    </div>
                    {entry.hint && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1 }}>{entry.hint}</div>}
                  </div>
                  {selected && <CornerDownLeft size={13} style={{ color: theme.color.faint, flexShrink: 0 }} />}
                </button>
              );
            })
          )}
        </div>

        <div style={{ padding: "8px 16px", borderTop: `1px solid ${theme.color.border}`, fontSize: 10, color: theme.color.faint, display: "flex", gap: 12 }}>
          <span><kbd style={{ padding: "1px 5px", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>Up</kbd> <kbd style={{ padding: "1px 5px", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>Down</kbd> navigate</span>
          <span><kbd style={{ padding: "1px 5px", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>Enter</kbd> open</span>
          <span style={{ marginLeft: "auto" }}>Ctrl/Cmd+K to reopen</span>
        </div>
      </div>
    </div>
  );
}
